import { ChatMessage } from "./ChatMessage";
import { ModelOutputMessageData } from "./message-data/ModelOutputMessageData";
import { PrimaryChatUserInputData } from "./message-data/PrimaryChatUserInputData";
import ModelOutputMessage from "../components/messages/ModelOutputMessage";
import { CitationIndex } from "../../sources/models/Citation";
import { MessageRole } from "../enums/MessageRole";
import { FunctionResultMessageData } from "./message-data/FunctionResultMessageData";

export type ChatMessageData =
  | PrimaryChatUserInputData
  | ModelOutputMessageData
  | FunctionResultMessageData;

type MessageDataAttributesType = {
  [key in MessageRole]: {
    constructor: (data: any) => ChatMessageData;
    component: (
      data: ChatMessageData,
      isInProgress?: boolean,
      onCitationClick?: (citation: CitationIndex) => void,
    ) => JSX.Element;
    isShown: boolean;
  };
};

const messageDataAttributes: MessageDataAttributesType = {
  [MessageRole.User]: {
    constructor: (data: any) => new PrimaryChatUserInputData(data),
    component: () => <></>,
    isShown: true,
  },
  [MessageRole.Assistant]: {
    constructor: (data: any) => new ModelOutputMessageData(data),
    component: (
      data: ChatMessageData,
      _isInProgress?: boolean,
      onCitationClick?: (citation: CitationIndex) => void,
    ) => (
      <ModelOutputMessage
        data={data as ModelOutputMessageData}
        onCitationClick={onCitationClick}
      />
    ),
    isShown: true,
  },
  [MessageRole.Function]: {
    constructor: (data: any) => new FunctionResultMessageData(data),
    component: () => <></>,
    isShown: true,
  },
};

export class MessageProcessor {
  static GetAttributes(role: MessageRole) {
    return messageDataAttributes[role];
  }

  static ParseData(role: MessageRole, data: any): ChatMessageData {
    return this.GetAttributes(role).constructor(data);
  }

  static GetComponent(
    role: MessageRole,
    data: ChatMessageData,
    isInProgress?: boolean,
    onCitationClick?: (citation: CitationIndex) => void,
  ): JSX.Element {
    const attributes = MessageProcessor.GetAttributes(role);
    return attributes.component(data, isInProgress, onCitationClick);
  }

  static getFunctionTitle(functionName: string): string {
    // Convert snake_case to Title Case
    return functionName
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  }

  static ProccessUpdate(
    messages: ChatMessage[],
    updated: ChatMessage,
  ): ChatMessage[] {
    const existingMessageIndex = messages.findIndex((m) => m.id === updated.id);
    if (existingMessageIndex !== -1) {
      messages[existingMessageIndex] = updated;
    } else {
      messages.push(updated);
    }
    const sortedSteps = messages.sort(
      (a, b) => +new Date(a.startedAt) - +new Date(b.startedAt),
    );
    return [...sortedSteps];
  }

  static ProcessContentUpdate(
    messages: ChatMessage[],
    messageId: string,
    content: string,
  ): ChatMessage[] {
    const message = messages.find((m) => m.id === messageId);
    if (!message) return messages;
    let messageData = message.data as ModelOutputMessageData;
    if (!messageData) {
      messageData = new ModelOutputMessageData({});
    }
    messageData.content = (messageData.content || "") + content;
    message.data = messageData;
    return MessageProcessor.ProccessUpdate(messages, message);
  }

  static showMessage(
    message: ChatMessage,
    isAdmin: boolean,
    isLast: boolean,
  ): boolean {
    // Show only UserInput and ModelOutput without function call
    if (isAdmin) return true;
    if (!message.finishedAt) return true;
    if (message.errorMessage && isLast) return true;
    if (!message.data) return true;

    if (message.role === MessageRole.User) return true;
    if (message.role === MessageRole.Assistant) {
      const data = message.data as ModelOutputMessageData;
      return !!data.content;
    }

    return this.GetAttributes(message.role).isShown;
  }
}
